import { PostgrestResponse } from "@supabase/supabase-js";
import { ISaldosAlunos } from "../../../types/tables/ISaldosAlunos";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Serviço para gerenciamento de saldos de alunos com isolamento multi-tenant
 * Gerencia créditos disponíveis para alunos gastarem na cantina
 */
export class SaldosAlunosService extends BaseMultiTenantService<ISaldosAlunos> {
  constructor() {
    super("saldos_alunos", [], "fk_id_organizacao");
  }

  /**
   * Busca saldo de um aluno específico
   */
  async getSaldoByAluno(
    organizationId: number,
    alunoId: number,
  ): Promise<PostgrestResponse<ISaldosAlunos>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_aluno", alunoId)
      .single();
  }

  /**
   * Adiciona crédito ao saldo do aluno
   */
  async adicionarCredito(
    organizationId: number,
    alunoId: number,
    responsavelId: number,
    valor: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    // Buscar saldo atual
    const { data: saldoAtualRaw } = await this.getSaldoByAluno(
      organizationId,
      alunoId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    let novoSaldo: number;
    let resultado: any;

    if (saldoAtual) {
      // Atualizar saldo existente
      novoSaldo = saldoAtual.saldo_disponivel + valor;
      resultado = await this.update(
        saldoAtual.id,
        {
          saldo_disponivel: novoSaldo,
          updated_at: new Date().toISOString(),
        },
        organizationId,
        userId,
      );
    } else {
      // Criar novo registro de saldo
      novoSaldo = valor;
      resultado = await this.create(
        {
          fk_id_aluno: alunoId,
          fk_id_responsavel: responsavelId,
          saldo_disponivel: novoSaldo,
          limite_diario: 0,
          limite_semanal: 0,
        },
        organizationId,
        userId,
      );
    }

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: alunoId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "credito",
      tipo_origem: "transferencia",
      origem_id: responsavelId,
      valor: valor,
      saldo_anterior: saldoAtual?.saldo_disponivel || 0,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Debita valor do saldo do aluno (para compras)
   */
  async debitarSaldo(
    organizationId: number,
    alunoId: number,
    valor: number,
    vendaId: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByAluno(
      organizationId,
      alunoId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Aluno não possui saldo cadastrado");
    }

    if (saldoAtual.saldo_disponivel < valor) {
      throw new Error("Saldo insuficiente");
    }

    const novoSaldo = saldoAtual.saldo_disponivel - valor;

    // Atualizar saldo
    const resultado = await this.update(
      saldoAtual.id,
      {
        saldo_disponivel: novoSaldo,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: alunoId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "debito",
      tipo_origem: "compra",
      origem_id: vendaId,
      valor: valor,
      saldo_anterior: saldoAtual.saldo_disponivel,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Define limites de gasto para o aluno
   */
  async definirLimites(
    organizationId: number,
    alunoId: number,
    limiteDiario: number,
    limiteSemanal: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByAluno(
      organizationId,
      alunoId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Aluno não possui saldo cadastrado");
    }

    return await this.update(
      saldoAtual.id,
      {
        limite_diario: limiteDiario,
        limite_semanal: limiteSemanal,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Verifica se o aluno pode realizar uma compra
   */
  async verificarLimiteCompra(
    organizationId: number,
    alunoId: number,
    valorCompra: number,
  ): Promise<{
    podeComprar: boolean;
    motivo?: string;
    saldoDisponivel: number;
    limiteDiarioRestante: number;
    limiteSemanalRestante: number;
  }> {
    const { data: saldoRaw } = await this.getSaldoByAluno(
      organizationId,
      alunoId,
    );
    const saldo = saldoRaw as any; // Conversão temporária

    if (!saldo) {
      return {
        podeComprar: false,
        motivo: "Aluno não possui saldo cadastrado",
        saldoDisponivel: 0,
        limiteDiarioRestante: 0,
        limiteSemanalRestante: 0,
      };
    }

    // Verificar saldo disponível
    if (saldo.saldo_disponivel < valorCompra) {
      return {
        podeComprar: false,
        motivo: "Saldo insuficiente",
        saldoDisponivel: saldo.saldo_disponivel,
        limiteDiarioRestante: 0,
        limiteSemanalRestante: 0,
      };
    }

    // Verificar limite diário
    if (saldo.limite_diario > 0) {
      const { data: gastosHoje } = await this.client.rpc(
        "calcular_gastos_periodo",
        {
          p_aluno_id: alunoId,
          p_organizacao_id: organizationId,
          p_data_inicio: new Date().toISOString().split("T")[0],
          p_data_fim: new Date().toISOString().split("T")[0],
        },
      );

      const gastoHoje = gastosHoje?.[0]?.total_gasto || 0;
      const limiteDiarioRestante = saldo.limite_diario - gastoHoje;

      if (limiteDiarioRestante < valorCompra) {
        return {
          podeComprar: false,
          motivo: "Limite diário excedido",
          saldoDisponivel: saldo.saldo_disponivel,
          limiteDiarioRestante,
          limiteSemanalRestante: 0,
        };
      }
    }

    // Verificar limite semanal
    if (saldo.limite_semanal > 0) {
      const hoje = new Date();
      const inicioSemana = new Date(
        hoje.setDate(hoje.getDate() - hoje.getDay()),
      );
      const fimSemana = new Date(
        hoje.setDate(hoje.getDate() - hoje.getDay() + 6),
      );

      const { data: gastosSemana } = await this.client.rpc(
        "calcular_gastos_periodo",
        {
          p_aluno_id: alunoId,
          p_organizacao_id: organizationId,
          p_data_inicio: inicioSemana.toISOString().split("T")[0],
          p_data_fim: fimSemana.toISOString().split("T")[0],
        },
      );

      const gastoSemana = gastosSemana?.[0]?.total_gasto || 0;
      const limiteSemanalRestante = saldo.limite_semanal - gastoSemana;

      if (limiteSemanalRestante < valorCompra) {
        return {
          podeComprar: false,
          motivo: "Limite semanal excedido",
          saldoDisponivel: saldo.saldo_disponivel,
          limiteDiarioRestante: 0,
          limiteSemanalRestante,
        };
      }
    }

    return {
      podeComprar: true,
      saldoDisponivel: saldo.saldo_disponivel,
      limiteDiarioRestante:
        saldo.limite_diario > 0 ? saldo.limite_diario : 999999,
      limiteSemanalRestante:
        saldo.limite_semanal > 0 ? saldo.limite_semanal : 999999,
    };
  }

  /**
   * Busca todos os saldos com informações dos alunos
   */
  async getAllWithAlunos(
    organizationId: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    aluno:fk_id_aluno(id, nome),
                    responsavel:fk_id_responsavel(id, nome)
               `,
      )
      .eq(this.organizationIdField, organizationId);
  }

  /**
   * Busca saldos dos alunos de um responsável específico
   */
  async getSaldosByResponsavel(
    organizationId: number,
    responsavelId: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    aluno:fk_id_aluno(id, nome)
               `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_responsavel", responsavelId);
  }

  /**
   * Relatório de consumo do aluno
   */
  async getRelatorioConsumo(
    organizationId: number,
    alunoId: number,
  ): Promise<PostgrestResponse<ISaldosAlunos[]>> {
    return await this.client
      .from("movimentacoes_saldo")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_pessoa", alunoId)
      .order("data_movimentacao", { ascending: false });
  }
}

export const saldosAlunosService = new SaldosAlunosService();
